import { useEffect } from "react";
import { 
    DEFAULT_AXNUI_CONTENT,
    DEFAULT_LANGUAGE
} from "@iddataweb/idw-common-ui/constants";
import {
    setActiveLanguage,
    setLanguageContent,
    setLanguageInitialized
} from "@redux/actions/language-actions";
import { useDispatch, useSelector } from "react-redux";
import { Logger } from "@utils/logger";
import {
    getAvailableLanguages,
    getConfigurationContent,
    getLocalizeFileNamespace,
    getLocalizeStrings,
    getMergeThemingAndLocalizedFile,
    getVerificationConfigurationState
} from "@redux/selectors/verification-configuration-selectors";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import {
    handleContentMerging,
    isFulfilled,
    isRejected,
    loadLanguageFile,
    extractConfigurationContentForLocaleType
} from "@iddataweb/idw-common-ui/utils";
import { DEFAULT_NAMESPACE } from "@constants/form-constants";
import { config as nodeConfig } from "node-config-ts";
import { BUCKET_NAME } from "@constants/language-config";

export const useInitializeLanguage = () => {
    const dispatch = useDispatch();
    const queryParametersState = useSelector(getQueryParametersState);
    const verificationConfigurationState = useSelector(getVerificationConfigurationState);
    const configurationContent = useSelector(getConfigurationContent);
    const localizeStrings = useSelector(getLocalizeStrings);
    const localizeFileNamespace = useSelector(getLocalizeFileNamespace);
    const mergeThemingAndLocalizedFile = useSelector(getMergeThemingAndLocalizedFile);
    const availableLanguages = useSelector(getAvailableLanguages);

    const initializeLanguage = async () => {
        const { config } = verificationConfigurationState;
        
        const { language } = queryParametersState;
        let languageToUse = language || config?.language || DEFAULT_LANGUAGE;

        //if only one available language, ignore the passed in value and use this instead
        if (availableLanguages.length === 1) {
            languageToUse = availableLanguages[0].code;
        }

        const namespaceToUse = localizeFileNamespace || DEFAULT_NAMESPACE;
        Logger.info(`[App] Language set to ${languageToUse}`);
        Logger.info(`[App] Namespace set to ${namespaceToUse}`);

        const [namespaceLanguageResponse] = await Promise.allSettled([
            namespaceToUse !== DEFAULT_NAMESPACE
                ? loadLanguageFile(BUCKET_NAME, languageToUse, namespaceToUse, nodeConfig.APPLICATION_ENVIRONMENT)
                : Promise.reject("No namespace language file is required")
        ]);

        let namespaceLanguageContent;
        if (isFulfilled(namespaceLanguageResponse)) {
            namespaceLanguageContent = namespaceLanguageResponse.value;
        } else if (isRejected(namespaceLanguageResponse)) {
            console.log(namespaceLanguageResponse.reason);
            namespaceLanguageContent = {};
        }

        const finalConfigurationContent = extractConfigurationContentForLocaleType(
            DEFAULT_AXNUI_CONTENT,
            configurationContent,
            languageToUse
        );

        const mergedContent = handleContentMerging<AXNUIContent>(
            localizeStrings,
            mergeThemingAndLocalizedFile,
            DEFAULT_AXNUI_CONTENT[DEFAULT_LANGUAGE],
            DEFAULT_AXNUI_CONTENT[languageToUse] || DEFAULT_AXNUI_CONTENT[DEFAULT_LANGUAGE],
            namespaceLanguageContent,
            finalConfigurationContent.data
        );
        dispatch(setLanguageContent(mergedContent));
        dispatch(setActiveLanguage(languageToUse));
        dispatch(setLanguageInitialized(true));
    };
    useEffect(() => {
        if (verificationConfigurationState.config) {
            initializeLanguage();
        }
    }, [verificationConfigurationState.config?.apikey]);
};